import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';

import { useTheme } from '@mui/material/styles';
import Avatar from '@mui/material/Avatar';
import Chip from '@mui/material/Chip';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemText from '@mui/material/ListItemText';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';

import { withAlpha } from 'utils/colorUtils';
import { IconRobot, IconUserPlus } from '@tabler/icons-react';

function ListItemWrapper({ children, onClick }) {
  const theme = useTheme();

  return (
    <Box
      onClick={onClick}
      sx={{
        p: 2,
        borderBottom: '1px solid',
        borderColor: 'divider',
        cursor: onClick ? 'pointer' : 'default',
        '&:hover': onClick
          ? {
              bgcolor: withAlpha(theme.palette.primary.main, 0.06),
              ...theme.applyStyles('dark', { bgcolor: 'dark.900' })
            }
          : undefined
      }}
    >
      {children}
    </Box>
  );
}

const ICONS = {
  bot_escalations: IconRobot,
  mm_onboarding: IconUserPlus
};

function NotificationIcon({ itemKey, priority }) {
  const theme = useTheme();
  const Icon = ICONS[itemKey] || IconRobot;
  const color =
    priority === 'high' ? 'error' : priority === 'medium' ? 'warning' : 'primary';

  return (
    <Avatar
      sx={{
        color: `${color}.dark`,
        bgcolor: `${color}.light`,
        ...theme.applyStyles('dark', { bgcolor: 'dark.main' })
      }}
    >
      <Icon stroke={1.5} size="20px" />
    </Avatar>
  );
}

export default function NotificationList({ items = [], onItemClick, onMarkAllRead }) {
  if (!items.length) {
    return (
      <Box sx={{ px: 2, py: 4, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          No action items right now.
        </Typography>
      </Box>
    );
  }

  return (
    <List sx={{ width: '100%', maxWidth: { xs: 300, md: 380 }, py: 0 }}>
      {items.map((item) => (
        <ListItemWrapper
          key={item.key}
          onClick={() => onItemClick?.(item)}
        >
          <ListItem
            alignItems="flex-start"
            disablePadding
            secondaryAction={
              item.age_label ? (
                <Typography variant="caption" color="text.secondary">
                  {item.age_label}
                </Typography>
              ) : null
            }
          >
            <ListItemAvatar>
              <NotificationIcon itemKey={item.key} priority={item.priority} />
            </ListItemAvatar>
            <ListItemText
              primary={
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="subtitle1">{item.title}</Typography>
                  {item.unread_count > 0 && (
                    <Chip label={item.unread_count} size="small" color="error" sx={{ height: 20, fontSize: '0.7rem' }} />
                  )}
                </Stack>
              }
            />
          </ListItem>
          <Stack sx={{ gap: 1, pl: 7, pt: 0.5 }}>
            <Typography variant="body2" color="text.secondary">
              {item.message}
            </Typography>
            {item.preview && (
              <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }} noWrap>
                “{item.preview}”
              </Typography>
            )}
            <Stack direction="row" spacing={0.5}>
              {item.unread_count > 0 && (
                <Chip label="New" color="error" size="small" variant="outlined" sx={{ width: 'min-content' }} />
              )}
              {item.priority === 'high' && (
                <Chip label="Urgent" color="warning" size="small" variant="outlined" sx={{ width: 'min-content' }} />
              )}
            </Stack>
          </Stack>
        </ListItemWrapper>
      ))}
    </List>
  );
}

ListItemWrapper.propTypes = {
  children: PropTypes.node,
  onClick: PropTypes.func
};

NotificationList.propTypes = {
  items: PropTypes.array,
  onItemClick: PropTypes.func,
  onMarkAllRead: PropTypes.func
};
